/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	Component,
	Input,
	OnDestroy,
	OnInit,
	Self,
	ChangeDetectionStrategy,
	Output,
	EventEmitter,
	ChangeDetectorRef,
	ViewChild,
	ElementRef,
} from '@angular/core';
import { ControlValueAccessor, UntypedFormBuilder, UntypedFormControl, NgControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as appStrings from '@touchplan/strings';

export interface IValidationError {
	errorCode: string;
	message: string;
}
export enum FileUploadState {
	Waiting = 'waiting',
	Loading = 'loading',
	Succeeded = 'succeeded',
	Failed = 'failed',
}
export enum UploadOutputFormat {
	BinaryString = 'binaryString',
	Text = 'text',
	DataUrl = 'base64',
	File = 'file',
}

/**
 * @deprecated Use TpFileUploadComponent
 */
@Component({
	selector: 'mat-file-upload',
	templateUrl: './file-upload.component.html',
	styleUrls: ['./file-upload.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LegacyFileUploadComponent implements ControlValueAccessor, OnInit, OnDestroy {
	@ViewChild('fileInput') public fileInput: ElementRef;
	@ViewChild('customFileInput') public customFileInput: ElementRef;

	/*
	 * For customUI supported inputs
	 *  -customUI
	 * 	-acceptedFileTypes
	 * 	-allowMultiple
	 *
	 * TODO: non-customUI part needs to be converted, so that every use case is same and differences is provided in ng-content
	 */
	@Input() public acceptedFileTypes = ['*']; // default is all files, can accept an array of string file extensions (eg. ['.xls', '.xlsx'])
	@Input() public allowMultiple = false;
	@Input() public placeholder = appStrings.FILE_UPLOAD_PLACEHOLDER;
	@Input() public startupMessage = appStrings.FILE_UPLOAD_STARTUP;
	@Input() public successMessage = appStrings.FILE_UPLOAD_SUCCESS;
	@Input() public failureMessage = appStrings.FILE_UPLOAD_FAILURE;
	@Input() public buttonText = appStrings.UICONTROL_TEXT_UPLOAD;
	@Input() public appearance = 'standard';
	@Input() public outputFormat: UploadOutputFormat = UploadOutputFormat.DataUrl;
	@Input() public validationErrors: IValidationError[] = []; // the types of validation errors on the file content that the user of this component can have
	@Output() public fileSelectedEvent = new EventEmitter<File[]>();

	public disable = false;
	public uploadedFiles: UntypedFormControl;
	public uploadedFilenames: UntypedFormControl;
	public strings = appStrings;
	public hintMessages: Map<FileUploadState, string>;

	public uploadState = FileUploadState.Waiting;
	private outputValue: any; // the content of the selected file or File object
	private shutdownSubject$ = new Subject<void>();
	private onChange: (value: any) => void;

	constructor(
		public ngControl: NgControl,
		private builder: UntypedFormBuilder,
		private changeDetectRef: ChangeDetectorRef
	) {
		// inject the FormControl the is bound to this component through FormControl or formControlName
		this.ngControl.valueAccessor = this;
		this.uploadedFiles = this.builder.control(null);
		this.uploadedFilenames = this.builder.control('');
	}

	public ngOnInit(): void {
		this.hintMessages = new Map<FileUploadState, string>([
			[FileUploadState.Waiting, this.startupMessage],
			[FileUploadState.Loading, ''], // could be used to show loading progress
			[FileUploadState.Succeeded, this.successMessage],
			[FileUploadState.Failed, this.failureMessage],
		]);

		// keep the validity of internal control with that of the external one so that mat-error will work for external validation errors
		this.ngControl.control.statusChanges.pipe(takeUntil(this.shutdownSubject$)).subscribe(() => {
			// if there is validation error and user asks that to be handled through the passed in validationErrors, sync the validity
			const controlErrors = this.ngControl.control.errors;
			if (controlErrors) {
				this.uploadState = FileUploadState.Failed;
				this.uploadedFilenames.markAsTouched();
			}
			this.uploadedFilenames.setErrors(!controlErrors || !this.getErrorMessage() ? null : controlErrors);
		});

		// start file uploading when file is selected
		this.uploadedFiles.valueChanges.pipe(takeUntil(this.shutdownSubject$)).subscribe((value) => {
			this.handleFileSelectionChange(value);
			if (this.fileInput) {
				this.fileInput.nativeElement.value = '';
			} else {
				this.customFileInput.nativeElement.value = '';
			}
		});
	}

	public ngOnDestroy() {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}

	public onFileChange(event: any): void {
		const files: File[] = event.target.files;
		const arr = Array.from(files); // FileList to Array
		this.uploadedFiles.patchValue(arr);
		this.uploadedFilenames.patchValue(
			arr.length < 2
				? arr.map((file: File) => file.name).join('')
				: `${arr.length} ${appStrings.FILES_SELECTED}`
		);
	}

	// loading file content when file is selected
	private handleFileSelectionChange(files: File[]) {
		let loadedFileCounter = 0;

		this.outputValue = [];
		if (files?.length > 0) {
			//file selected event - to get filenames
			this.fileSelectedEvent.emit(files);

			for (const file of files) {
				const reader = new FileReader();
				switch (this.outputFormat) {
					case UploadOutputFormat.BinaryString:
						reader.readAsBinaryString(file);
						break;
					case UploadOutputFormat.Text:
						reader.readAsText(file);
						break;
					case UploadOutputFormat.File:
						this.outputValue.push(file);
						loadedFileCounter++;
						break;
					default:
						reader.readAsDataURL(file);
						break;
				}

				if (this.outputFormat !== UploadOutputFormat.File) {
					reader.onload = (result) => {
						this.uploadState = (result.target as FileReader).error
							? FileUploadState.Failed
							: FileUploadState.Succeeded;
						if (this.uploadState === FileUploadState.Succeeded) {
							this.outputValue.push((result.target as FileReader).result);

							// update form when all files are uploaded
							loadedFileCounter++;
							if (loadedFileCounter === files.length) {
								this.onChange(this.outputValue);
								// now the value updated and validation triggered, needs to keep the internal status reflected on UI
								this.changeDetectRef.detectChanges();
							}
						} else {
							this.onChange(null);
						}
					};
				}
			}

			// also need here for sync operation
			if (loadedFileCounter === files.length) {
				this.onChange(this.outputValue);
				// now the value updated and validation triggered, needs to keep the internal status reflected on UI
				this.changeDetectRef.detectChanges();
			}
		} else {
			this.onChange(null);
			this.outputValue = null;
			this.uploadState = FileUploadState.Waiting;
		}
	}

	public getErrorMessage(): string {
		let message;

		// the first validation error in the given list that can be found in the controls' errors list is displayed
		for (const error of this.validationErrors) {
			if (this.ngControl.control.hasError(error.errorCode)) {
				message = error.message;
				break;
			}
		}

		return message;
	}

	/**************************** ControlValueAccessor  *******************************/
	public writeValue(value: any): void {
		// this component sends file content out. The value it accepts is to clear the content
		this.uploadedFilenames.setValue(value?.name, { emitEvent: false });
		if (!value) {
			this.outputValue = null;
		}
	}

	public registerOnChange(fn: (value: any) => void): void {
		this.onChange = fn;
	}

	public registerOnTouched(fn: any): void {
		// we do not do anything here at this time
	}

	public setDisabledState?(isDisabled: boolean): void {
		this.disable = isDisabled;
		if (this.disable) {
			this.uploadedFiles.disable({ emitEvent: false });
		} else {
			this.uploadedFiles.enable({ emitEvent: false });
		}
	}
}
